import type { Condition } from '@medplum/fhirtypes';

import { hasPendingReviewTag } from './review-status';

/**
 * Condition-native review semantics (the `verificationStatus` axis) on top of
 * the cross-type review-status tag. Declared in a shared lib, not a frontend
 * feature lib: api-nest derives the same membership rules as the patient
 * profile's problem list, and a `feature`-tagged lib is unreachable from the
 * backend. Transition source of truth:
 * `libs/backend/mini-apps/.../clinical-findings/finding-types/condition.ts`.
 */
export const CONDITION_VER_STATUS_SYSTEM =
  'http://terminology.hl7.org/CodeSystem/condition-ver-status';

/** The condition's verification code (provisional, confirmed, refuted, …). */
export function verificationCode(condition: Condition): string | undefined {
  return condition.verificationStatus?.coding?.[0]?.code;
}

/** Retracted (dismissed / superseded) — off the problem list everywhere. */
export function isRetracted(condition: Condition): boolean {
  return verificationCode(condition) === 'entered-in-error';
}

/**
 * An explicitly denied finding ("denies chest pain") the agent wrote as
 * `refuted`. While pending it still needs review (attesting the denial was
 * correctly captured); once reviewed it is not a problem-list entry.
 */
export function isRefuted(condition: Condition): boolean {
  return verificationCode(condition) === 'refuted';
}

/**
 * Still awaiting human review: the uniform `review-status: pending` tag, or —
 * for conditions written outside the agent pipeline — a bare
 * `provisional`/`unconfirmed` verificationStatus (ticket scope treats both as
 * unverified). Retracted resources are never pending, even when a stale
 * pending tag lingers after a supersede.
 */
export function isPendingReview(condition: Condition): boolean {
  if (isRetracted(condition)) return false;
  const verification = verificationCode(condition);
  if (verification === 'provisional' || verification === 'unconfirmed') {
    return true;
  }
  return hasPendingReviewTag(condition);
}

/** The supporting transcript passage stored on a finding (first note.text). */
export function readEvidence(condition: Condition): string | null {
  return condition.note?.[0]?.text ?? null;
}
